import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Bold,
  Italic,
  Underline,
  Type,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Smile,
  Link,
} from 'lucide-react';
import TextSizeDropdown from './TextSizeDropdown';
import { Tooltip as UiTooltip } from '@leapeasy/ui-kit';

const ToolbarContainer = styled.div`
  display: flex;
  gap: ${(props) => (props.compact ? '8px' : '0.5rem')};
  padding: ${(props) => (props.compact ? '8px 12px' : '1rem')};
  flex-wrap: ${(props) => (props.compact ? 'nowrap' : 'wrap')};
  align-items: center;
  justify-content: ${(props) => (props.compact ? 'space-between' : 'flex-start')};
  overflow: visible;
  border: 1px solid ${(props) => (props.compact ? '#EAEAEA' : 'rgba(0, 0, 0, 0.35)')} !important;
  border-radius: 8px;
  margin-bottom: ${(props) => (props.compact ? '0' : '1rem')};
  position: relative;
  z-index: 3;
`;

const ToolGroup = styled.div`
  display: flex;
  gap: ${(props) => (props.compact ? '8px' : '0.25rem')};
  padding-right: ${(props) => (props.compact ? '0' : '0.5rem')};
  margin-right: ${(props) => (props.compact ? '0' : '0.5rem')};
  border-right: ${(props) => (props.compact ? 'none' : '1px solid #eee')};
  position: relative;
  font-size: 16px !important;
  flex-shrink: 0;
`;

const CompactBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  // gap: 8px;
  width: 100%;
  overflow: visible;
`;

const ToolButton = styled.button`
  width: ${(props) => (props.compact ? '32px' : 'auto')};
  height: ${(props) => (props.compact ? '32px' : 'auto')};
  padding: ${(props) => (props.compact ? '8px' : '0.5rem')};
  border: none;
  background: ${(props) => (props.active ? '#E8D6EB' : '#FFFFFF')};
  border-radius: 6px;
  cursor: pointer;
  color: ${(props) => (props.active ? '#90388B' : '#6A5E71')};
  transition: all 0.2s;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: #e8d6eb;
    color: #90388b;
  }

  svg {
    width: ${(props) => (props.compact ? '16px' : '20px')};
    height: ${(props) => (props.compact ? '16px' : '20px')};
  }
`;

const ToolbarTooltipButton = ({ tooltip, children, ...props }) => (
  <UiTooltip title={tooltip} placement="top" showCarot enterDelay={350}>
    <div>
      <ToolButton aria-label={tooltip} {...props}>
        {children}
      </ToolButton>
    </div>
  </UiTooltip>
);
const Toolbar = ({ activeStyles, onCommand, onEmojiClick, showEmojiPicker, compact = false }) => {
  const [showTextSize, setShowTextSize] = useState(false);

  const handleLinkClick = () => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      const url = prompt('Enter URL:');
      if (url) {
        try {
          new URL(url);
          onCommand('createLink', url);
        } catch {
          alert('Please enter a valid URL (including http:// or https://)');
        }
      }
    } else {
      alert('Please select some text first to create a link');
    }
  };

  const handleTextSize = (size) => {
    onCommand('fontSize', size);
    setShowTextSize(false);
  };

  if (compact) {
    return (
      <ToolbarContainer compact={compact}>
        <CompactBar>
          <ToolbarTooltipButton
            compact
            active={activeStyles.has('bold')}
            onClick={() => onCommand('bold')}
            tooltip="Bold"
          >
            <Bold />
          </ToolbarTooltipButton>
          <ToolbarTooltipButton
            compact
            active={activeStyles.has('italic')}
            onClick={() => onCommand('italic')}
            tooltip="Italic"
          >
            <Italic />
          </ToolbarTooltipButton>
          <ToolbarTooltipButton
            compact
            active={activeStyles.has('underline')}
            onClick={() => onCommand('underline')}
            tooltip="Underline"
          >
            <Underline />
          </ToolbarTooltipButton>
          <ToolGroup compact>
            <ToolbarTooltipButton
              compact
              active={showTextSize}
              onClick={() => setShowTextSize(!showTextSize)}
              tooltip="Text size"
            >
              <Type />
            </ToolbarTooltipButton>
            {showTextSize && <TextSizeDropdown onSelect={handleTextSize} />}
          </ToolGroup>
          <ToolbarTooltipButton
            compact
            active={activeStyles.has('justifyLeft')}
            onClick={() => onCommand('justifyLeft')}
            tooltip="Align left"
          >
            <AlignLeft />
          </ToolbarTooltipButton>
          <ToolbarTooltipButton
            compact
            active={showEmojiPicker}
            onClick={onEmojiClick}
            tooltip="Insert emoji"
          >
            <Smile />
          </ToolbarTooltipButton>
          <ToolbarTooltipButton
            compact
            active={activeStyles.has('insertOrderedList')}
            onClick={() => onCommand('insertOrderedList')}
            tooltip="Numbered list"
          >
            <ListOrdered />
          </ToolbarTooltipButton>
          <ToolbarTooltipButton
            compact
            active={activeStyles.has('justifyCenter')}
            onClick={() => onCommand('justifyCenter')}
            tooltip="Align center"
          >
            <AlignCenter />
          </ToolbarTooltipButton>
          <ToolbarTooltipButton compact onClick={handleLinkClick} tooltip="Insert link">
            <Link />
          </ToolbarTooltipButton>
        </CompactBar>
      </ToolbarContainer>
    );
  }

  return (
    <ToolbarContainer compact={compact}>
      <ToolGroup compact={compact}>
        <ToolbarTooltipButton
          compact={compact}
          active={activeStyles.has('bold')}
          onClick={() => onCommand('bold')}
          tooltip="Bold"
        >
          <Bold />
        </ToolbarTooltipButton>
        <ToolbarTooltipButton
          compact={compact}
          active={activeStyles.has('italic')}
          onClick={() => onCommand('italic')}
          tooltip="Italic"
        >
          <Italic />
        </ToolbarTooltipButton>
        <ToolbarTooltipButton
          compact={compact}
          active={activeStyles.has('underline')}
          onClick={() => onCommand('underline')}
          tooltip="Underline"
        >
          <Underline />
        </ToolbarTooltipButton>
      </ToolGroup>

      <ToolGroup compact={compact}>
        <ToolbarTooltipButton
          compact={compact}
          active={showTextSize}
          onClick={() => setShowTextSize(!showTextSize)}
          tooltip="Text size"
        >
          <Type />
        </ToolbarTooltipButton>
        {showTextSize && <TextSizeDropdown onSelect={handleTextSize} />}
      </ToolGroup>

      <ToolGroup compact={compact}>
        <ToolbarTooltipButton
          compact={compact}
          active={activeStyles.has('justifyLeft')}
          onClick={() => onCommand('justifyLeft')}
          tooltip="Align left"
        >
          <AlignLeft />
        </ToolbarTooltipButton>
        <ToolbarTooltipButton
          compact={compact}
          active={activeStyles.has('justifyCenter')}
          onClick={() => onCommand('justifyCenter')}
          tooltip="Align center"
        >
          <AlignCenter />
        </ToolbarTooltipButton>
        <ToolbarTooltipButton
          compact={compact}
          active={activeStyles.has('justifyRight')}
          onClick={() => onCommand('justifyRight')}
          tooltip="Align right"
        >
          <AlignRight />
        </ToolbarTooltipButton>
        <ToolbarTooltipButton
          compact={compact}
          active={activeStyles.has('justifyFull')}
          onClick={() => onCommand('justifyFull')}
          tooltip="Justify"
        >
          <AlignJustify />
        </ToolbarTooltipButton>
      </ToolGroup>

      <ToolGroup compact={compact}>
        <ToolbarTooltipButton
          compact={compact}
          active={activeStyles.has('insertUnorderedList')}
          onClick={() => onCommand('insertUnorderedList')}
          tooltip="Bullet list"
        >
          <List />
        </ToolbarTooltipButton>
        <ToolbarTooltipButton
          compact={compact}
          active={activeStyles.has('insertOrderedList')}
          onClick={() => onCommand('insertOrderedList')}
          tooltip="Numbered list"
        >
          <ListOrdered />
        </ToolbarTooltipButton>
      </ToolGroup>

      <ToolGroup compact={compact}>
        <ToolbarTooltipButton compact={compact} onClick={handleLinkClick} tooltip="Insert link">
          <Link />
        </ToolbarTooltipButton>
        <ToolbarTooltipButton
          compact={compact}
          active={showEmojiPicker}
          onClick={onEmojiClick}
          tooltip="Insert emoji"
        >
          <Smile />
        </ToolbarTooltipButton>
      </ToolGroup>
    </ToolbarContainer>
  );
};

export default Toolbar;
